import { wsMessageTypeEnum } from '@hooks/useWebsocketTypes';
import { ErrorResponseType } from '@nauTypes/reduxTypes';
import {
  END_CALL,
  GET_CALL_DATA,
  SET_PARTICIPANTS_PERMITS,
  SET_PARTICIPANT_STATE,
  SET_PARTICIPANT_STATUS,
} from '@redux/constants/calls';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  CallParticipant,
  CallStatusEnum,
  CallType,
  CallTypeApi,
  SetCallParticipantPermitsPayload,
} from 'types/callType';
import { DialogType } from 'types/chatsType';
import { RootState } from 'types/stateTypes';
import { setDevicesInitialValues } from 'utils/call.utils';
import { getCaller } from 'utils/chats.utils.typed';
import { upsertImmutable } from 'utils/common';
import safeFetch from '../../shared/api/safeFetch';
import {
  GetParticipantsPayload,
  IncomingCallType,
  RedialType,
  SetParticipantStateProps,
  SetParticipantStatusPayload,
  endCallFailed,
  endCallSuccess,
  getCallDataFailed,
  getCallDataSuccess,
  getParticipantsFailed,
  getParticipantsSuccess,
  handleIncomingCallFailed,
  handleIncomingCallSuccess,
  setParticipantsPermitsFailed,
  setParticipantsPermitsSuccess,
  terminateCall,
} from '../actions/callActions';
import { getDialogInfoSuccess } from '../actions/chatsActions';
import { ActionType } from '../actions/themeActions';
import { CALL_HANDLE_INCOMING, CALL_REDIAL } from '../constants/index';

const BASE_CALL_URL = '/api/v1/calls';
const BASE_CHAT_URL = '/api/chat/v1/dialogs';

const fetchCallParticipant = async ({
  dialog,
  userId,
}: {
  dialog: GetParticipantsPayload['dialog'];
  userId: string | number;
}): Promise<CallParticipant> => {
  const result = await safeFetch(`${BASE_CALL_URL}/dialog/${dialog}/participant?userId=${userId}`, {
    headers: { access: '*/*' },
    method: 'GET',
  });
  return result?.data as CallParticipant;
};

const setParticipantState = async (action: ActionType<SetParticipantStateProps>): Promise<{ data: unknown[] }> => {
  const { dialogId, statePayload } = action.payload;
  const result = await safeFetch(`${BASE_CALL_URL}/dialog/${dialogId}/state`, {
    body: statePayload,
    headers: { 'Content-Type': 'application/json', access: '*/*' },
    method: 'PATCH',
  });
  return { data: (result?.data as unknown[]) ?? [] };
};

function* handleIncomingCall(action: ActionType<IncomingCallType>) {
  const { cType, dialogId: chatId, callerId } = action.payload;
  let dialogId = chatId;
  const { auth }: RootState = yield select();
  const { call: callState }: RootState = yield select();
  const userId = auth.id;
  const callStatus = callState.status;
  const isAvailable = callStatus === CallStatusEnum.finish;
  const isWaitingInLobby = callState.isWaitingInLobby;
  const isCall =
    cType === wsMessageTypeEnum.audioCallStarted ||
    cType === wsMessageTypeEnum.videoCallStarted ||
    cType === wsMessageTypeEnum.callUsers;
  const isCancelCall = cType === wsMessageTypeEnum.callEnded;
  const isIncomingCall = isCall && callerId !== userId && !isWaitingInLobby;
  if (typeof dialogId === 'string') dialogId = Number(dialogId);
  if (dialogId) {
    try {
      if (isCall) {
        const { data } = yield call(safeFetch, `${BASE_CHAT_URL}/info/${dialogId}?details=2`, {
          headers: { access: '*/*' },
          method: 'GET',
        });
        yield put(getDialogInfoSuccess(data));
        const caller = getCaller({ dialog: data, userId: callerId, users: data.users });
        const isConference = data.type === DialogType.group;

        // До типизации было такое присвоение. Нужно будет проверить на корректность, когда вернемся к работе со звонками.
        // const isConference = dialogData?.dialogType === DialogType.group || dialogData?.dialogType === DialogType.group;

        if (isIncomingCall && isAvailable) {
          yield put(handleIncomingCallSuccess({ caller, conference: isConference, dialogId }));
        }
      }
      if (isCancelCall) yield put(terminateCall());
    } catch (error) {
      yield put(handleIncomingCallFailed((error as Error).message));
    }
  }
}

function* handleRedial(action: ActionType<RedialType>) {
  const { dialogId, userId } = action.payload;
  try {
    yield call(safeFetch, `${BASE_CALL_URL}/invite/dialog/${dialogId}`, {
      body: {
        users: [userId],
      },
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'POST',
    });
  } catch (error) {}
}

function* handleEndCall(action: ActionType<CallType['dialogId']>) {
  const dialogId = action.payload;
  try {
    yield call(safeFetch, `${BASE_CALL_URL}/dialog/${dialogId}/stop`, {
      body: {
        dialog: dialogId,
      },
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'POST',
    });
    yield put(endCallSuccess());
  } catch (error: unknown) {
    yield put(endCallFailed({ message: 'endCallFailed' }));
  }
}

function* handleGetCallData(action: ActionType<GetParticipantsPayload>) {
  const { dialog } = action.payload;
  const { flashphoner }: RootState = yield select();
  try {
    const { data } = yield call(safeFetch, `${BASE_CALL_URL}/dialog/${dialog}`, {
      headers: { access: '*/*' },
      method: 'GET',
    });
    const { id, startDate, duration, participants } = data as CallTypeApi;
    const updatedParticipants = setDevicesInitialValues({
      callParticipants: participants,
      roomParticipants: flashphoner.roomParticipants,
    });
    yield put(getCallDataSuccess({ duration, id, startDate }));
    yield put(getParticipantsSuccess(updatedParticipants));
  } catch (error) {
    const err = error as ErrorResponseType;
    yield put(getCallDataFailed({ message: err?.message || '' }));
    yield put(getParticipantsFailed({ message: err?.message || '' }));
  }
}

function* handleSetParticipantStatus(action: ActionType<SetParticipantStatusPayload>) {
  const { dialog, user, status } = action.payload;
  try {
    yield call(safeFetch, `${BASE_CALL_URL}/dialog/${dialog}/status`, {
      body: { status, user },
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'PATCH',
    });
  } catch (error) {
    const err = error as ErrorResponseType;
    console.error(err);
  }
}

function* handleSetParticipantsPermits(action: ActionType<SetCallParticipantPermitsPayload>) {
  const { permitsData, dialog } = action.payload;
  try {
    yield call(safeFetch, `${BASE_CALL_URL}/dialog/${dialog}/permits`, {
      body: permitsData,
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'PATCH',
    });
    yield put(setParticipantsPermitsSuccess({}));
  } catch (error) {
    setParticipantsPermitsFailed({});
    const err = error as ErrorResponseType;
    console.error(err);
  }
}

function* handleSetParticipantState(action: ActionType<SetParticipantStateProps>) {
  const { dialogId, statePayload } = action.payload;
  const { call: callStore }: RootState = yield select();
  if (!statePayload?.streamName) {
    try {
      const participant: CallParticipant = yield call(fetchCallParticipant, {
        dialog: dialogId,
        userId: statePayload.user,
      });
      const participants = upsertImmutable(callStore.callParticipants, participant, 'id');
      yield put(getParticipantsSuccess(participants));
      const streamName = participant?.states?.[0]?.streamName;
      if (streamName)
        yield call(setParticipantState, {
          payload: { dialogId, statePayload: { ...statePayload, streamName } },
          type: action.type,
        });
    } catch (error) {
      const err = error as ErrorResponseType;
      console.error(err);
    }
  } else {
    yield call(setParticipantState, action);
  }
}

export default function* createChatWatcher(): Generator {
  yield takeEvery(CALL_HANDLE_INCOMING, handleIncomingCall);
  yield takeEvery(CALL_REDIAL, handleRedial);
  yield takeEvery(END_CALL, handleEndCall);
  yield takeEvery(GET_CALL_DATA, handleGetCallData);
  yield takeEvery(SET_PARTICIPANT_STATUS, handleSetParticipantStatus);
  yield takeEvery(SET_PARTICIPANT_STATE, handleSetParticipantState);
  yield takeEvery(SET_PARTICIPANTS_PERMITS, handleSetParticipantsPermits);
}
